'use client';

import { useCallback } from 'react';

import type { AuthChangeEvent } from '@supabase/supabase-js';

import { AnalyticsUserProperties } from '@kit/analytics';
import { useMonitoring } from '@kit/monitoring/hooks';
import { useAppEvents } from '@kit/shared/events';
import { useAuthChangeListener } from '@kit/supabase/hooks/use-auth-change-listener';

import pathsConfig from '~/config/paths.config';

export function AuthProvider(props: React.PropsWithChildren) {
  const dispatchEvent = useDispatchAppEventFromAuthEvent();
  const monitoring = useMonitoring();

  const onSpuriousSignOut = useCallback(
    (event: AuthChangeEvent) => {
      monitoring.captureEvent('[Auth] Spurious sign-out event ignored', {
        event,
      });
    },
    [monitoring],
  );

  // This is the single mount that owns the redirect/reload behaviour. The other
  // mount (CreditProvider) passes redirectOnSignOut: false so a sign-out event
  // is not acted on twice.
  useAuthChangeListener({
    appHomePath: pathsConfig.app.home,
    onSpuriousSignOut,
    onEvent: (event, session) => {
      const companyRole =
        typeof session?.user.user_metadata?.companyRole === 'string'
          ? session.user.user_metadata.companyRole
          : undefined;

      dispatchEvent(event, session?.user.id, {
        ...(companyRole
          ? {
              [AnalyticsUserProperties.CompanyRole]: companyRole,
            }
          : {}),
      });
    },
  });

  return props.children;
}

function useDispatchAppEventFromAuthEvent() {
  const { emit } = useAppEvents();
  const monitoring = useMonitoring();

  return useCallback(
    (
      type: string,
      userId: string | undefined,
      traits: Record<string, string> = {},
    ) => {
      switch (type) {
        case 'SIGNED_IN':
          if (userId) {
            emit({
              type: 'user.signedIn',
              payload: { userId, ...traits },
            });

            monitoring.identifyUser({ id: userId, ...traits });
          }

          break;

        case 'USER_UPDATED':
          emit({
            type: 'user.updated',
            payload: { userId: userId!, ...traits },
          });

          break;
      }
    },
    [emit, monitoring],
  );
}
